import React, {useMemo, useState} from 'react';
import {classes, style, vars} from './doppe-action-type-coupon-view.st.css';
import {Button, Icon, reactCreateMemo } from '@wix/devzai-utils-react';
import IconCopy from './icons/copy.svg';
import {CommonClasses, FlexLayout} from '@wix/devzai-common-client';
import {clipboardCopy} from '@wix/devzai-utils-dom';
import {useDoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services';
import {BymoPageViewerMode, useBymoPageViewerContext} from '../bymo-page-viewer-context/bymo-page-viewer-context';
import {DoppeDtoAction} from '../../client-server-common/types/doppe-dto-action';
import {useCurrentPageId} from '../../client/doppe-viewer-app-client-services/doppe-viewer-router-client-service-hooks';
import {colorBlend, Values} from '@wix/devzai-utils-common';
import {useDoppeViewerLocales} from "../../client/doppe-viewer-locales/doppe-viewer-locales";
import {BymoPageStyleScopeColors, useBymoPageScopeColor} from "../bymo-page-style/bymo-page-style";


export const CouponSourceType = {
    Action: 'Action',
    CouponAction: 'CouponAction',
    SearchLink: 'SearchLink',
    ShortLink: 'ShortLink',
} as const;

export type CouponSourceType = Values<typeof CouponSourceType>;

const Features = {
    useMainColorForHover: true
}

export const DoppeActionTypeCouponView = reactCreateMemo(function DoppeActionTypeCouponView(props: DoppeActionTypeCouponView.Props) {
    const {
        coupon,
        couponSourceType,
        actionId,
        onCouponCopied,

        style: styleProp,
        className,
        ...htmlAttributes
    } = props;

    const [isCopied, setIsCopied] = useState(false);

    const textColor = useBymoPageScopeColor(BymoPageStyleScopeColors.TextColor);
    const mainColor = useBymoPageScopeColor(BymoPageStyleScopeColors.MainColor);
    const backgroundColor = useBymoPageScopeColor(BymoPageStyleScopeColors.BackgroundColor);
    const textHoverColor = useMemo(() => {
        return colorBlend(textColor, backgroundColor, 0.7) ?? textColor;
    }, [textColor, backgroundColor])

    const clientServices = useDoppeViewerClientServices();
    const pageViewerContext = useBymoPageViewerContext();
    const doppeViewerLocales = useDoppeViewerLocales();
    const innerPageId = useCurrentPageId();

    return (
        <div
            {...htmlAttributes}
            style={{
                ...styleProp,
                [vars.textColor]: textColor,
                [vars.textHoverColor]: Features.useMainColorForHover ? mainColor : textHoverColor
            }}
            className={style(classes.root, {
                copied: isCopied
            }, className, FlexLayout.row.alignToCenter, CommonClasses.button)}
            onClick={() => {
                clipboardCopy(coupon);
                setTimeout(() => {
                    setIsCopied(false)
                }, 3000);
                setIsCopied(true);

                if (pageViewerContext.mode === BymoPageViewerMode.Live) {
                    clientServices.viewerAnalyticsClient.logEvent('uouCouponCopied', {
                        hypeUserId: pageViewerContext.doppeUserId,
                        linkId: pageViewerContext.linkId,
                        pageId: pageViewerContext.bymoPageId,
                        type: couponSourceType,
                        actionId: actionId,
                        innerPageId: innerPageId,
                    });

                }

                onCouponCopied?.();
            }}
        >
            <div
                className={style(classes.couponText, {}, FlexLayout.fillRemainingHorizontalSpace, CommonClasses.ellipsis)}
            >
                {isCopied ? doppeViewerLocales['copiedToClipboard'] : coupon}
            </div>
            <Icon
                className={style(classes.copyIcon, {})}
                icon={IconCopy}
            />
        </div>
    );
});

export namespace DoppeActionTypeCouponView {

    export interface Props extends Button.Props {
        coupon: string;
        couponSourceType: CouponSourceType;
        actionId?: DoppeDtoAction.Id;
        onCouponCopied?: () => void;
    }
}
